import { useLazyQuery, useQuery } from "@apollo/client";
import { withJsonFormsMultiEnumProps } from "@jsonforms/react";
import { debounce } from "lodash";
import { LoadingSpinner, Modal } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  listSegmentsPaginated,
  loadSegmentSummary,
} from "../../../graphql/queries/listSegments";
import ButtonGroupDynamic from "../../common/ButtonGroupDynamic";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import SegmentSelect from "./SegmentSelect";
import "./_campaign-segment-select.scss";

const CampaignSegmentSelectComponent = (props) => {
  const { handleChange, path, data, uischema } = props;
  const isMultipleSelection = uischema.options?.multiple !== false;

  const [openModal, setOpenModal] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;
  const filterRef = useRef({ keyword: "", status: "ENABLED" });

  const { data: segmentsData, refetch } = useQuery(listSegmentsPaginated, {
    variables: {
      filter: { status: "ENABLED", keyword: undefined },
      limit,
      offset: 0,
    },
  });
  const [loadSegmentSummaryCall] = useLazyQuery(loadSegmentSummary, {
    fetchPolicy: "cache-first",
  });

  const segments = segmentsData?.listSegmentsPaginated?.items || [];
  const totalCount = segmentsData?.listSegmentsPaginated?.totalCount || 0;
  const totalPages = useMemo(
    () => Math.ceil(totalCount / limit),
    [totalCount, limit]
  );

  useEffect(() => {
    if (!data?.length) return;
    const ids = isMultipleSelection ? data : typeof data === "string" ? [data] : [];
    setLoading(true);
    Promise.all(
      ids.map((id) =>
        loadSegmentSummaryCall({
          variables: { id },
          fetchPolicy: "cache-first",
        })
      )
    )
      .then((results) => {
        const savedData = results.map((result) => {
          const segment = result?.data?.loadSegment;
          if (segment?.status === "ENABLED") {
            return segment;
          } else {
            return {
              ...segment,
              name: "Selected segment is no longer active",
              status: "DISABLED",
            };
          }
        });
        setSelectedSegments(savedData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isMultipleSelection]);

  const onChangeFilters = useMemo(
    () =>
      debounce(() => {
        setCurrentPage(1);
        refetch({
          filter: {
            status: filterRef.current.status,
            keyword: filterRef.current.keyword || undefined,
          },
          limit,
          offset: 0,
        });
      }, 500),
    [refetch, limit]
  );

  const handleSearch = (value) => {
    filterRef.current.keyword = value;
    onChangeFilters();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch({
      filter: {
        status: filterRef.current.status,
        keyword: filterRef.current.keyword || undefined,
      },
      limit,
      offset: (page - 1) * limit,
    });
  };

  const handleToggleModal = () => setOpenModal(!openModal);
  const handleCloseModal = () => {
    handleToggleModal();
  };
  const handleSegmentSelection = (selected) => setSelectedSegments(selected);
  const handleSaveSegments = () => {
    const segmentIds = selectedSegments?.map((s) => s.id);
    const value = isMultipleSelection
      ? segmentIds
      : segmentIds.length > 0
        ? segmentIds[0]
        : "";
    handleChange(path, value);
    handleToggleModal();
    openSuccessToast(selectedSegments);
  };
  const handleRemoveSaved = (id) => {
    const filtered = selectedSegments.filter((s) => s.id !== id);
    setSelectedSegments(filtered);
  };
  const openSuccessToast = (newSegments) => {
    const formatNames = (names) => {
      if (names.length === 0) return "";
      if (names.length === 1) return names[0];
      const lastName = names.pop();
      return `${names.join(", ")} and ${lastName}`;
    };
    const newSelected = formatNames(newSegments.map((s) => s.name));
    setToast({
      open: true,
      type: "success",
      title: `${newSelected} segment added`,
      description: isMultipleSelection
        ? "You have just added segments!"
        : "You have just updated the segment selection!",
    });
  };

  return (
    <div className="segment-select">
      {loading && (
        <div>
          <LoadingSpinner style={{ height: "24px", width: "24px" }} />
        </div>
      )}
      {!loading &&
        selectedSegments.map((segment) => (
          <div
            className={`segment-item display-flex flex-direction-row font-xs font-bold segment-added ${
              segment.status === "DISABLED" ? "segment-disabled" : ""
            }`}
            key={segment.id}
          >
            <div className="added-segment display-flex flex-direction-row align-items-center justify-content-space-between">
              <span>{segment.name}</span>
              <ButtonGroupDynamic
                size="small"
                items={[
                  {
                    icon: "delete",
                    onClick: () => handleRemoveSaved(segment.id),
                  },
                ]}
              />
            </div>
          </div>
        ))}
      <div className="segment-item display-flex flex-direction-row font-xs font-bold">
        <ButtonGroupDynamic
          size="small"
          items={[{ icon: "add", onClick: handleToggleModal }]}
        />
        Add New Segment
      </div>
      <ToastNotification
        {...toast}
        handleClose={() => setToast({ ...toast, open: false })}
      />
      <Modal
        className="campaign-segment-select-modal"
        showModal={openModal}
        title="Choose Segment"
        text=""
        showSecondaryButton={false}
        showTertiaryButton={false}
        showLink={false}
        onCloseModal={handleCloseModal}
        primaryButtonProps={{
          text: "Save",
          disabled: !selectedSegments.length,
          onClick: handleSaveSegments,
        }}
      >
        <SegmentSelect
          segmentsData={segments}
          selected={selectedSegments}
          onChange={handleSegmentSelection}
          selection={isMultipleSelection ? "multiple" : "single"}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
        />
      </Modal>
    </div>
  );
};

CampaignSegmentSelectComponent.propTypes = {
  handleChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
  uischema: PropTypes.shape({
    options: PropTypes.shape({
      multiple: PropTypes.bool,
    }),
  }),
};

export const CampaignSegmentSelectControl = withJsonFormsMultiEnumProps(
  CampaignSegmentSelectComponent
);
